// AGE-BUG(date-picker-mudChange-escapes-shadow-root) · vendor/age-design/SOURCE_COMMIT 7322a157
// The shared guard for the second `mudChange` a date input fires when a day is
// picked from its calendar.
//
// `mud-date-input` renders `mud-date-picker` inside its own shadow root and
// listens for the picker's `mudChange`. Its handler translates the ISO day into
// the masked display value and emits its OWN `mudChange`, carrying the contract
// consumers are documented to read: `{ value, isoValue }`. But Stencil's
// `@Event()` defaults to `bubbles + composed`, so the picker's original event
// does not stop at the shadow boundary — it continues into the light DOM and
// arrives at the very same `<MudDateInput onMudChange>` handler, one tick later,
// carrying the picker's own shape: `{ value }` for a single date, plus
// `{ rangeStart, rangeEnd }` in range mode. No `isoValue` at all.
//
// So a consumer written exactly as AGE documents it —
// `setDate(e.detail.isoValue ?? '')` — is called twice per selection, and the
// SECOND call, the one that wins, reads `undefined` and clears the field. The
// calendar opens, navigates and closes correctly; the date it selected is what
// never lands. Typing the date by hand is unaffected: `handleChange` on blur
// fires only the input's own event.
//
// `target === currentTarget`, the primary test in `onModalDismiss`, cannot
// separate these two: a composed event is retargeted to the host it escaped
// from, so BOTH events name `mud-date-input` as their target. The detail shape
// is the only discriminator, and it is a sound one — `isoValue` is the property
// `mud-date-input` was given to carry and `mud-date-picker` has never had.
//
// Nothing is fixed upstream: the vendored tarball is 7322a157, and there is
// nothing newer to vendor. The day AGE stops bubbling this event,
// `grep -rn 'AGE-BUG(date-picker-mudChange-escapes-shadow-root)'` finds this
// helper and every one of its call sites, and all of it can be deleted.

/**
 * `mudChange` as `mud-date-input` itself emits it: the committed display value
 * and the ISO `YYYY-MM-DD` behind it, `null` when what stands in the field is
 * incomplete, malformed, or outside `min`/`max`.
 */
export type DateCommitDetail = {
  value: string;
  isoValue: string | null;
};

/**
 * Wraps a `<MudDateInput onMudChange>` handler so it runs once per commit, for
 * the input's own event, and stays silent for the picker's event that merely
 * escaped the input's shadow root.
 *
 * @example
 * ```tsx
 * // AGE-BUG(date-picker-mudChange-escapes-shadow-root) · vendor/age-design/SOURCE_COMMIT
 * // 7322a157 — picking a day fires `mudChange` twice at this handler; the second
 * // one is the picker's own, and it carries no `isoValue`.
 * <MudDateInput onMudChange={onDateCommit((d) => setDate(d.isoValue ?? ''))} />
 * ```
 *
 * The guard only declines to call the handler. It never stops the event, so
 * anything else listening still sees both.
 */
export function onDateCommit(
  handler: (detail: DateCommitDetail) => void,
): (ev: CustomEvent<unknown>) => void {
  return (ev) => {
    const detail = ev.detail;
    if (typeof detail !== 'object' || detail === null) return;
    // Presence, not truthiness: `isoValue` is legitimately `null` whenever the
    // field holds something that is not a date, and that commit must still
    // reach the handler so it can clear the value it is bound to.
    if (!('isoValue' in detail)) return;
    handler(detail as DateCommitDetail);
  };
}
